// Five-layer parallax backdrop (sky → foreground) plus atmospheric fog bands.
// Every layer is a screen-space TileSprite scrolled by camera.scrollX × PARALLAX[layer];
// layers are scaled so the texture height covers the viewport plus a small margin
// that absorbs a gentle vertical drift with camera.scrollY.

import type Phaser from 'phaser';
import { DEPTH, GAME_HEIGHT, GAME_WIDTH, PARALLAX, PARALLAX_LAYERS, type ParallaxLayer } from '../../config/constants';
import type { SectorDef } from '../../core/sector';
import { fogTex, parallaxTex } from './textures';

interface Layer {
  sprite: Phaser.GameObjects.TileSprite;
  factor: number;
  scale: number;
  /** Vertical drift budget in px (the sprite is this much taller than the viewport on each side). */
  margin: number;
  /** Extra downward offset (foreground sits low so it frames rather than covers the floor). */
  drop: number;
}

const LAYER_DEPTH: Record<ParallaxLayer, number> = {
  sky: DEPTH.BG_SKY,
  far: DEPTH.BG_FAR,
  mid: DEPTH.BG_MID,
  near: DEPTH.BG_NEAR,
  fore: DEPTH.FOREGROUND,
};
const LAYER_MARGIN: Record<ParallaxLayer, number> = { sky: 12, far: 30, mid: 48, near: 72, fore: 40 };
/** Vertical drift as a share of the horizontal factor (vertical parallax is kept subtle). */
const VERTICAL_SHARE = 0.45;
const FORE_ALPHA = 0.8;

export class Parallax {
  private layers: Layer[] = [];
  private fogs: Phaser.GameObjects.Image[] = [];
  private midScrollY: number;

  constructor(scene: Phaser.Scene, sector: SectorDef, roomHeight: number) {
    this.midScrollY = Math.max(0, roomHeight - GAME_HEIGHT) / 2;
    for (const layer of PARALLAX_LAYERS) {
      const key = parallaxTex(scene, sector, layer);
      const frame = scene.textures.getFrame(key);
      const texH = Math.max(1, frame?.realHeight ?? GAME_HEIGHT);
      const margin = LAYER_MARGIN[layer];
      const h = GAME_HEIGHT + margin * 2;
      const scale = h / texH;
      const sprite = scene.add
        .tileSprite(0, -margin, GAME_WIDTH, h, key)
        .setOrigin(0, 0)
        .setScrollFactor(0)
        .setDepth(LAYER_DEPTH[layer])
        .setTileScale(scale, scale);
      if (layer === 'fore') sprite.setAlpha(FORE_ALPHA);
      this.layers.push({ sprite, factor: PARALLAX[layer], scale, margin, drop: layer === 'fore' ? 36 : 0 });
    }

    // Fog bands between the distant layers and in front of the near layer separate the planes.
    const fog = fogTex(scene);
    const band = (depth: number, alpha: number, y: number, h: number) =>
      this.fogs.push(
        scene.add
          .image(0, y, fog)
          .setOrigin(0, 0)
          .setDisplaySize(GAME_WIDTH, h)
          .setScrollFactor(0)
          .setDepth(depth)
          .setTint(sector.palette.fog)
          .setAlpha(alpha),
      );
    band(DEPTH.BG_FAR + 1, 0.55, GAME_HEIGHT * 0.25, GAME_HEIGHT * 0.75);
    band(DEPTH.BG_MID + 1, 0.45, GAME_HEIGHT * 0.4, GAME_HEIGHT * 0.6);
    band(DEPTH.BG_NEAR + 1, 0.35, GAME_HEIGHT * 0.55, GAME_HEIGHT * 0.45);
  }

  update(cam: Phaser.Cameras.Scene2D.Camera): void {
    const dy = cam.scrollY - this.midScrollY;
    for (const l of this.layers) {
      l.sprite.tilePositionX = (cam.scrollX * l.factor) / l.scale;
      const drift = Math.max(-l.margin, Math.min(l.margin, dy * l.factor * VERTICAL_SHARE));
      l.sprite.y = -l.margin - drift + l.drop;
    }
  }

  destroy(): void {
    for (const l of this.layers) l.sprite.destroy();
    for (const f of this.fogs) f.destroy();
    this.layers = [];
    this.fogs = [];
  }
}
